import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import NotificationBell from '../components/NotificationBell';
import { 
  Sparkles, ArrowRight, CheckCircle, Lock, Users, 
  BookOpen, Headphones, PenTool, Mic, Star, Rocket
} from 'lucide-react';

const LevelSelection = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);

  // Auto-redirect logged-in users to their dashboard
  useEffect(() => {
    if (user && !authLoading) {
      if (user.selected_level) {
        navigate(`/level/${user.selected_level}`);
      }
    }
  }, [user, authLoading, navigate]);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevels = async () => {
    try {
      const response = await api.get('/levels');
      setLevels(response.data);
    } catch (error) {
      console.error('Error fetching levels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLevel = async (level) => {
    if (user) {
      try {
        await api.post('/auth/select-level', { level });
        navigate(`/level/${level}`);
      } catch (error) {
        console.error('Error selecting level:', error);
      }
    } else {
      // Redirect to login with level in state
      const redirectUrl = window.location.origin + `/level/${level}`;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  const levelColors = {
    A2: { gradient: "from-green-400 to-green-600", bg: "bg-green-50", border: "border-green-200" },
    B1: { gradient: "from-blue-400 to-blue-600", bg: "bg-blue-50", border: "border-blue-200" },
    B2: { gradient: "from-purple-400 to-purple-600", bg: "bg-purple-50", border: "border-purple-200" }
  };

  const moduleIcons = [
    { icon: Headphones, name: "Listening" },
    { icon: BookOpen, name: "Reading" },
    { icon: PenTool, name: "Writing" },
    { icon: Mic, name: "Speaking" }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFCF8]/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
              DELF Success Academy
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/partners">
              <Button variant="ghost" className="font-medium" data-testid="refer-earn-btn">💰 Refer & Earn</Button>
            </Link>
            <Link to="/quiz">
              <Button variant="ghost" className="font-medium">Take Quiz</Button>
            </Link>
            <Link to="/pricing">
              <Button className="rounded-full bg-amber-400 hover:bg-amber-500 text-gray-900 font-semibold border-0 shadow-sm">View Pricing</Button>
            </Link>
            {user && <NotificationBell />}
            {!user && (
              <Button onClick={handleLogin} className="rounded-full px-6 font-semibold" data-testid="login-btn">
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="bg-primary rounded-2xl px-8 py-10 md:px-12 md:py-12 shadow-lg shadow-primary/20 mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-3 text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
              Pass Your DELF Exam With Confidence.
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl font-semibold text-white/90" style={{ fontFamily: 'Syne, sans-serif' }}>
              First-Time Success&mdash;Or Your Money Back.
            </p>
          </div>
          <p className="text-base font-bold text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            AI-powered corrections, 40 targeted exercises, realistic exam simulations, and a proven preparation system designed to help you become exam-ready in as little as 30 days.
            <br className="hidden md:block" />
            No guesswork. No wasted time.
            <br />
            Just structured preparation that builds confidence before exam day.
          </p>
        </motion.div>

        {/* Choose Your Level Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
            Choose Your Level
          </h2>
          <div className="text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed space-y-3">
            <p>Choose the DELF level you are preparing for. Each program includes four complete preparation modules:</p>
            <ul className="text-left max-w-md mx-auto space-y-1">
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" /> Compr&eacute;hension de l&rsquo;Oral (Listening)</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" /> Compr&eacute;hension des &Eacute;crits (Reading)</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" /> Production Orale (Speaking)</li>
              <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" /> Production &Eacute;crite (Writing)</li>
            </ul>
            <p>Each module contains 10 carefully designed exercises that closely reflect the official DELF examination format.</p>
            <p className="font-semibold text-foreground">40 targeted exercises per level with AI-powered feedback and progress tracking.</p>
          </div>
          <div className="mt-4">
            <Link to="/quiz" className="text-primary font-medium hover:underline inline-flex items-center gap-1">
              Not sure? Take our free placement quiz <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>

        {/* Levels Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {levels.map((level, index) => (
            <motion.div
              key={level.level}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative bg-white rounded-3xl border-2 ${levelColors[level.level].border} overflow-hidden hover:shadow-xl transition-all duration-300`}
            >
              {/* Level Badge */}
              <div className={`h-24 bg-gradient-to-r ${levelColors[level.level].gradient} flex items-center justify-center`}>
                <span className="text-5xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {level.level}
                </span>
              </div>

              <div className="p-6">
                <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {level.name}
                </h2>
                <p className="text-muted-foreground text-sm mb-4">{level.subtitle_fr}</p>
                <p className="text-sm mb-6">{level.description}</p>

                {/* Modules Preview */}
                <div className="grid grid-cols-4 gap-2 mb-6">
                  {moduleIcons.map((mod, i) => (
                    <div key={i} className={`p-3 rounded-xl ${levelColors[level.level].bg} flex items-center justify-center`}>
                      <mod.icon className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
                    </div>
                  ))}
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-muted-foreground mb-6 pb-6 border-b">
                  <span>{level.modules_count} modules</span>
                  <span>{level.exercises_per_module * level.modules_count} exercises</span>
                </div>

                {/* CTA Button */}
                <Button
                  onClick={() => handleSelectLevel(level.level)}
                  className={`w-full rounded-full py-6 font-bold text-lg bg-gradient-to-r ${levelColors[level.level].gradient} hover:opacity-90 transition-opacity`}
                  data-testid={`select-${level.level}-btn`}
                >
                  {level.is_unlocked ? (
                    <>
                      Continue <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  ) : (
                    <>
                      Start Now <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                {!level.is_unlocked && (
                  <p className="text-center text-xs text-muted-foreground mt-3">
                    Free preview available
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12 bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8 border border-primary/20"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Join 2,500+ successfully prepared students</h3>
                <p className="text-muted-foreground text-sm">95% of our students pass their DELF exam on the first try</p>
              </div>
            </div>
            <Link to="/pricing">
              <Button className="rounded-full bg-amber-400 hover:bg-amber-500 text-gray-900 font-semibold border-0 shadow-sm">
                View All Plans
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Student Success Stories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16"
          data-testid="student-success-stories-section"
        >
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 text-amber-700 text-sm font-medium mb-4 border border-amber-200">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              Verified Student Success Stories
            </div>
            <p className="text-muted-foreground text-sm mb-2">
              Trusted by students preparing for DELF exams around the world
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>
              Student Success Stories
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Real results from students who prepared with DELF Success Academy
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                name: 'Clara D.',
                country: 'Germany',
                flag: '🇩🇪',
                level: 'B2',
                quote: 'The AI writing feedback pinpointed exactly what examiners look for. I went from struggling with essays to scoring 94 on my DELF B2.',
                photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&h=120&fit=crop&crop=face'
              },
              {
                name: 'Lucas M.',
                country: 'Brazil',
                flag: '🇧🇷',
                level: 'B1',
                quote: 'I only had 60 days to prepare. The personalized study plan kept me on track every day. Listening exercises were exactly like the real exam.',
                photo: 'https://images.unsplash.com/photo-1531750026848-8ada78f641c2?w=120&h=120&fit=crop&crop=face'
              },
              {
                name: 'Mei-Lin T.',
                country: 'Japan',
                flag: '🇯🇵',
                level: 'A2',
                quote: 'As a complete beginner, the step-by-step exercises built my confidence. The speaking practice with AI feedback was a game changer.',
                photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=120&h=120&fit=crop&crop=face'
              },
              {
                name: 'Sofia R.',
                country: 'Colombia',
                flag: '🇨🇴',
                level: 'B2',
                quote: 'The writing module taught me exactly how examiners grade. My production écrite score went from 12 to 22 out of 25. Absolutely worth it.',
                photo: 'https://images.unsplash.com/photo-1662850886700-4ec19bd30d11?w=120&h=120&fit=crop&crop=face'
              },
              {
                name: 'Noah W.',
                country: 'United Kingdom',
                flag: '🇬🇧',
                level: 'B1',
                quote: 'I tried other apps but nothing was DELF-specific. This platform understands the exam format perfectly. The reading strategies saved me so much time.',
                photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&h=120&fit=crop&crop=face'
              },
              {
                name: 'Arjun K.',
                country: 'India',
                flag: '🇮🇳',
                level: 'A2',
                quote: 'Learning French from Mumbai with no local classes was tough. This platform made it possible — the daily exercises and progress tracking helped me pass A2 on my first try.',
                photo: 'https://images.unsplash.com/photo-1625241152315-4a698f74ceb7?w=120&h=120&fit=crop&crop=face'
              }
            ].map((t, index) => {
              const badgeColors = {
                A2: 'text-green-700 bg-green-100',
                B1: 'text-blue-700 bg-blue-100',
                B2: 'text-purple-700 bg-purple-100'
              };
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="bg-white rounded-2xl p-6 border border-border/60 hover:shadow-lg hover:border-primary/20 transition-all duration-300"
                  data-testid={`success-story-${index}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.photo}
                      alt={t.name}
                      className="w-11 h-11 rounded-full object-cover ring-2 ring-border shadow-sm"
                      loading="lazy"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-sm">{t.name}</span>
                        <span className="text-xs text-muted-foreground">{t.flag} {t.country}</span>
                      </div>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${badgeColors[t.level]}`}>
                        DELF {t.level}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-0.5 mb-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>

                  <p className="text-sm text-gray-600 leading-relaxed">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
            data-testid="success-stories-cta"
          >
            <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-5" style={{ fontFamily: 'Syne, sans-serif' }}>
              Ready to Become Our Next DELF Success Story?
            </h3>
            <Button
              size="lg"
              className="rounded-full px-10 py-6 font-bold text-[15px] shadow-lg shadow-primary/25"
              onClick={() => navigate('/pricing')}
              data-testid="success-stories-cta-btn"
            >
              <Rocket className="w-5 h-5 mr-2" />
              Start My DELF Success Now
            </Button>
          </motion.div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border bg-white">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-6">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="font-semibold">DELF Success Academy</span>
          </div>
          <div className="text-center">
            <p className="font-semibold text-base mb-1">Need Help Fast?</p>
            <p className="text-sm text-muted-foreground mb-3">Our team is ready to assist you.</p>
            <a
              href="https://wa.me/message/OLMYDM6IORLZF1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] hover:bg-[#1fb855] text-white font-semibold text-sm shadow-md transition-colors"
              data-testid="whatsapp-contact-btn"
            >
              💬 Chat with Us on WhatsApp
            </a>
          </div>
          <p className="text-sm text-muted-foreground">
            &copy; 2026 DELF Success Academy. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LevelSelection;
